import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Phone, ArrowRight, Home, BadgeCheck, UserCheck, Clock, Star, Syringe, Bandage, Droplets, TestTube, Pill, Heart } from 'lucide-react';
import { siteConfig, nursingServices, advantages, serviceAreas, testimonials } from '../data/mockData';

export default function HomePage() {
  const mainServices = nursingServices.slice(0, 6);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-amber-50 via-white to-neutral-50 overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtNi42MjcgMC0xMiA1LjM3My0xMiAxMnM1LjM3MyAxMiAxMiAxMiAxMi01LjM3MyAxMi0xMi01LjM3My0xMi0xMi0xMnoiIHN0cm9rZT0iI0Y1OUUwQiIgc3Ryb2tlLW9wYWNpdHk9Ii4xIiBzdHJva2Utd2lkdGg9IjIiLz48L2c+PC9zdmc+')] opacity-30"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-block px-4 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-medium">
                Conventionnée INAMI • Tiers payant
              </div>
              
              <h1 className="text-4xl lg:text-5xl font-bold text-neutral-900 leading-tight">
                Infirmière à domicile à Fleurus, Sambreville et Farciennes
              </h1>
              
              <p className="text-lg text-neutral-600 leading-relaxed">
                Soins infirmiers professionnels dans le confort de votre domicile. 
                Approche humaine, écoute attentive et suivi personnalisé pour votre bien-être.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <a href={`tel:${siteConfig.phone}`}>
                  <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white w-full sm:w-auto">
                    <Phone className="h-5 w-5 mr-2" />
                    Appeler maintenant
                  </Button>
                </a>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-neutral-300 w-full sm:w-auto">
                    Demande de soins
                    <ArrowRight className="h-5 w-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-amber-200 to-amber-100 rounded-2xl opacity-20 blur-2xl"></div>
              <img
                src="https://images.pexels.com/photos/7551686/pexels-photo-7551686.jpeg"
                alt="Infirmière prodiguant des soins à domicile"
                className="relative rounded-2xl shadow-2xl w-full object-cover"
                style={{ aspectRatio: '4/3' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-neutral-900 mb-4">
              Soins infirmiers à domicile
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              Une gamme complète de soins professionnels réalisés dans le confort de votre domicile
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mainServices.map((service) => {
              const iconMap = {
                'injections': Syringe,
                'wound-care': Bandage,
                'personal-hygiene': Droplets,
                'blood-samples': TestTube,
                'medication-management': Pill,
                'elderly-care': Heart
              };
              const IconComponent = iconMap[service.id] || Heart;
              
              return (
                <Card key={service.id} className="group hover:shadow-lg transition-all duration-300 border-neutral-200 hover:border-amber-200">
                  <CardContent className="p-6">
                    <div className="bg-gradient-to-br from-amber-50 to-amber-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <IconComponent className="w-6 h-6 text-amber-700" />
                    </div>
                    
                    <h3 className="text-xl font-semibold text-neutral-900 mb-2">
                      {service.title}
                    </h3>
                    
                    <p className="text-neutral-600 mb-4 leading-relaxed">
                      {service.description}
                    </p>
                    
                    {service.inami && (
                      <div className="inline-flex items-center text-xs text-amber-700 bg-amber-50 px-3 py-1 rounded-full">
                        <BadgeCheck className="h-3 w-3 mr-1" />
                        Remboursé INAMI
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <Link to="/soins">
              <Button size="lg" variant="outline" className="border-amber-600 text-amber-700 hover:bg-amber-50">
                Voir tous les soins
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-neutral-50 to-amber-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-neutral-900 mb-4">
              Pourquoi choisir mes services ?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {advantages.map((advantage, index) => {
              const IconComponent = {
                Home,
                BadgeCheck,
                UserCheck,
                Clock,
              }[advantage.icon] || Home;

              return (
                <div key={advantage.id} className="text-center">
                  <div className="bg-white w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm">
                    <IconComponent className="h-8 w-8 text-amber-700" />
                  </div>
                  <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                    {advantage.title}
                  </h3>
                  <p className="text-neutral-600 leading-relaxed">
                    {advantage.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-neutral-900 mb-4">
              Témoignages de patients
            </h2>
            <p className="text-lg text-neutral-600">
              La confiance de mes patients est ma plus belle récompense
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-neutral-200">
                <CardContent className="p-6">
                  <div className="flex items-center mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 text-amber-500 fill-amber-500" />
                    ))}
                  </div>
                  
                  <p className="text-neutral-700 mb-4 leading-relaxed italic">
                    "{testimonial.comment}"
                  </p>
                  
                  <div className="flex items-center justify-between text-sm">
                    <div>
                      <p className="font-semibold text-neutral-900">{testimonial.name}</p>
                      <p className="text-neutral-500">{testimonial.location}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Presentation */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-amber-50 via-white to-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.pexels.com/photos/5207117/pexels-photo-5207117.jpeg"
                alt="Infirmière professionnelle"
                className="rounded-2xl shadow-xl w-full object-cover"
                style={{ aspectRatio: '3/4' }}
              />
            </div>
            
            <div className="space-y-6">
              <h2 className="text-3xl lg:text-4xl font-bold text-neutral-900">
                Une approche humaine et professionnelle
              </h2>
              
              <p className="text-lg text-neutral-700 leading-relaxed">
                Infirmière diplômée avec plus de 10 ans d'expérience, je mets mon expertise au service 
                des patients de Fleurus, Sambreville et Farciennes.
              </p>
              
              <p className="text-neutral-600 leading-relaxed">
                Ma priorité est de vous offrir des soins de qualité dans le respect de votre autonomie 
                et de votre dignité. Je travaille en étroite collaboration avec votre médecin traitant 
                et votre famille pour assurer un suivi optimal.
              </p>
              
              <Link to="/a-propos">
                <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white">
                  En savoir plus
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-neutral-900 mb-4">
              Zone d'intervention
            </h2>
            <p className="text-lg text-neutral-600">
              Je me déplace dans les communes suivantes
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {serviceAreas.map((area) => (
              <Card key={area.id} className="border-neutral-200 hover:border-amber-300 hover:shadow-lg transition-all">
                <CardContent className="p-6 text-center">
                  <h3 className="text-2xl font-bold text-neutral-900 mb-2">{area.name}</h3>
                  <p className="text-amber-700 font-medium mb-3">Code postal: {area.postalCode}</p>
                  <p className="text-sm text-neutral-600 leading-relaxed">{area.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Link to="/zone">
              <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white">
                Voir la carte
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-r from-amber-600 to-amber-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Besoin de soins infirmiers à domicile ?
          </h2>
          <p className="text-xl text-amber-50 mb-8">
            Contactez-moi dès maintenant pour une prise en charge rapide et professionnelle
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${siteConfig.phone}`}>
              <Button size="lg" className="bg-white text-amber-700 hover:bg-amber-50 w-full sm:w-auto">
                <Phone className="h-5 w-5 mr-2" />
                {siteConfig.phone}
              </Button>
            </a>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-amber-600 w-full sm:w-auto">
                Formulaire de contact
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
